import { useState, useCallback, useEffect } from 'react'
import { fetchObjectDefs } from '../../../utils/decoupled'

/**
 * Slugifie un libellé en machine name snake_case.
 *
 *   "Article"          → "article"
 *   "Page Section"     → "page_section"
 *   "Étiquette d'info" → "etiquette_d_info"
 */
export const generateMachineName = (label) =>
  (label || '')
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    || `type_${Date.now()}`

/**
 * Hook pour lister, créer, mettre à jour et supprimer les types d'objet
 * (`builder_object_def`).
 *
 * Source de vérité : la collection decoupled des `builder_def` de kind
 * `object` (`fetchObjectDefs`).
 * La création/édition passe par /api/builder/object-def/save (`create: true`
 * pour une création, `nid` pour une mise à jour). La suppression refuse si des
 * instances existent encore — l'appelant doit purger ou migrer avant.
 */
export const useBuilderObjectTypes = () => {
  const [types, setTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setTypes(await fetchObjectDefs())
    } catch (e) {
      setError(e)
      setTypes([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  /**
   * Crée ou met à jour un type de contenu. `definition` est le YAML brut ;
   * `siteId` le site où il naît (création seulement).
   *
   * `create: true` dit au back que le geste est une CRÉATION : il refuse alors
   * (409) un nom machine déjà pris au lieu d'écraser le type qui le porte. Sans
   * ce drapeau, un appel sans `nid` reste un find-or-update par `type`.
   *
   * @returns {object} `{nid, type, name}` du type sauvegardé
   */
  const saveType = useCallback(async ({ nid, type, name, definition, create, siteId }) => {
    const res = await fetch('/api/builder/object-def/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        nid,
        type,
        name,
        definition,
        ...(create ? { create: true } : {}),
        // Le site où NAÎT le type — lu à la création seulement : le back
        // l'ignore sur une mise à jour (déplacer un type passe par
        // /api/builder/def/site).
        ...(siteId !== undefined ? { siteId } : {}),
      }),
    })
    const data = await res.json()
    if (!res.ok || !data.success) {
      throw new Error(data.message || `Type save error: ${res.status}`)
    }
    // Mise à jour optimiste de la liste locale.
    setTypes(prev => {
      const idx = prev.findIndex(t => t.type === data.type)
      const entry = { nid: data.nid, type: data.type, name: data.name, definition }
      if (idx === -1) return [...prev, entry]
      const next = [...prev]
      next[idx] = entry
      return next
    })
    return data
  }, [])

  /**
   * Supprime un type par nid (ou par machine name en fallback). Échoue
   * (409) si des instances l'utilisent encore.
   */
  const deleteType = useCallback(async ({ nid, type }) => {
    const res = await fetch('/api/builder/object-def/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ nid, type }),
    })
    const data = await res.json()
    if (!res.ok || !data.success) {
      throw new Error(data.message || `Type delete error: ${res.status}`)
    }
    setTypes(prev => prev.filter(t => (nid ? t.nid !== nid : t.type !== type)))
    return data
  }, [])

  return { types, loading, error, saveType, deleteType, reload: load }
}

/**
 * Hook utilitaire pour récupérer un seul type d'objet par machine name.
 * Polit le ré-fetch quand le composant change de type sans déclencher de
 * navigation. Cache local minimal.
 */
export const useBuilderObjectType = (machineName) => {
  const [type, setType] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!machineName) {
      setType(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const all = await fetchObjectDefs()
      setType(all.find(t => t.type === machineName) || null)
    } catch (e) {
      setError(e)
      setType(null)
    } finally {
      setLoading(false)
    }
  }, [machineName])

  useEffect(() => { load() }, [load])

  return { type, loading, error, reload: load }
}
